// Offline Contentful Client - reads from exported JSON files
class OfflineContentfulClient {
  constructor() {
    this.entries = {};
    this.assets = {};
    this.loaded = false;
  }

  async loadData() {
    if (this.loaded) return;

    try {
      // Load all entries
      const entriesResponse = await fetch('/entries-manifest.json');
      if (entriesResponse.ok) {
        const entriesManifest = await entriesResponse.json();
        for (const entryId of entriesManifest.entries || []) {
          try {
            const entryResponse = await fetch(`/entries/${entryId}.json`);
            if (entryResponse.ok) {
              const entryData = await entryResponse.json();
              this.entries[entryId] = entryData;
            }
          } catch (e) {
            console.warn(`Failed to load entry ${entryId}:`, e);
          }
        }
      }

      // Load all assets
      const assetsResponse = await fetch('/assets-manifest.json');
      if (assetsResponse.ok) {
        const assetsManifest = await assetsResponse.json();
        for (const assetId of assetsManifest.assets || []) {
          try {
            const assetResponse = await fetch(`/assets/${assetId}.json`);
            if (assetResponse.ok) {
              const assetData = await assetResponse.json();
              this.assets[assetId] = assetData;
            }
          } catch (e) {
            console.warn(`Failed to load asset ${assetId}:`, e);
          }
        }
      }

      this.loaded = true;
      console.log('Offline data loaded:', { entries: Object.keys(this.entries).length, assets: Object.keys(this.assets).length });
    } catch (error) {
      console.error('Failed to load offline data:', error);
    }
  }

  resolveLinks(data, depth = 0, maxDepth = 10) {
    if (depth > maxDepth) return data;

    if (Array.isArray(data)) {
      return data.map(item => this.resolveLinks(item, depth + 1, maxDepth));
    }

    if (data && typeof data === 'object') {
      if (data.sys && data.sys.type === 'Link') {
        const linkType = data.sys.linkType;
        const id = data.sys.id;

        if (linkType === 'Entry' && this.entries[id]) {
          return this.resolveLinks(this.entries[id], depth + 1, maxDepth);
        } else if (linkType === 'Asset' && this.assets[id]) {
          const asset = JSON.parse(JSON.stringify(this.assets[id]));
          // Rewrite asset URLs to local paths
          if (asset.fields && asset.fields.file && asset.fields.file.url) {
            const url = asset.fields.file.url;
            const filename = url.split('/').pop();
            // Look for the downloaded asset file
            asset.fields.file.url = `/assets/${id}_${filename}`;
          }
          return this.resolveLinks(asset, depth + 1, maxDepth);
        }

        return data;
      }

      const resolved = {};
      for (const key in data) {
        resolved[key] = this.resolveLinks(data[key], depth + 1, maxDepth);
      }
      return resolved;
    }

    return data;
  }

  async getEntry(entryId, options = {}) {
    await this.loadData();

    const entry = this.entries[entryId];
    if (!entry) {
      throw new Error(`Entry ${entryId} not found in offline data`);
    }

    const include = options.include || 1;
    return this.resolveLinks(JSON.parse(JSON.stringify(entry)), 0, include);
  }

  async getEntries(query = {}) {
    await this.loadData();

    let items = Object.values(this.entries);

    // Filter by content type
    if (query.content_type) {
      items = items.filter(entry => 
        entry.sys && entry.sys.contentType && 
        entry.sys.contentType.sys && 
        entry.sys.contentType.sys.id === query.content_type
      );
    }

    // Apply other filters as needed
    // (add more filtering logic here if needed)

    const include = query.include || 1;
    const resolvedItems = items.map(item => 
      this.resolveLinks(JSON.parse(JSON.stringify(item)), 0, include)
    );

    return {
      sys: { type: 'Array' },
      total: resolvedItems.length,
      skip: 0,
      limit: resolvedItems.length,
      items: resolvedItems
    };
  }

  async getAsset(assetId) {
    await this.loadData();

    const asset = this.assets[assetId];
    if (!asset) {
      throw new Error(`Asset ${assetId} not found in offline data`);
    }

    // Rewrite asset URL to local path
    const assetCopy = JSON.parse(JSON.stringify(asset));
    if (assetCopy.fields && assetCopy.fields.file && assetCopy.fields.file.url) {
      const url = assetCopy.fields.file.url;
      const filename = url.split('/').pop();
      assetCopy.fields.file.url = `/assets/${assetId}_${filename}`;
    }

    return assetCopy;
  }
}

// Create and export the offline client
const offlineClient = new OfflineContentfulClient();

export default offlineClient;
